import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { CollectionPageSEO } from '@/components/feature/SEOHead';
import { seoArticles } from '@/mocks/seo-articles';

export default function ShoppingGuide() {
  return (
    <>
      <CollectionPageSEO
        title="Complete LitBuy Spreadsheet Shopping Guides"
        description="Browse our comprehensive collection of LitBuy Spreadsheet guides, tutorials, and resources. Each article is crafted to help you become a smarter, more efficient shopper across all 15+ product categories."
        slug="/shopping-guide"
      />
      <Navbar />
      <main>
        {/* Hero */}
        <section className="pt-24 pb-12 md:pb-16 bg-background-100">
          <div className="w-full px-4 md:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <span className="inline-block px-3 py-1 text-xs font-medium bg-accent-100 text-accent-700 rounded-full mb-4">
                Shopping Guide
              </span>
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900">
                Complete LitBuy Spreadsheet Shopping Guides
              </h1>
              <p className="mt-4 text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
                Browse our comprehensive collection of LitBuy Spreadsheet guides, tutorials, and resources. Each article is crafted to help you become a smarter, more efficient shopper across all 15+ product categories.
              </p>
            </div>
          </div>
        </section>

        {/* Article Grid */}
        <section className="w-full py-12 md:py-16 bg-background-50">
          <div className="w-full px-4 md:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-1 gap-5 md:gap-6">
                {/* Primary article - What is LitBuy */}
                <Link
                  to="/what-is-litbuy-spreadsheet"
                  className="group flex flex-col sm:flex-row gap-5 p-5 md:p-6 bg-background-100 rounded-lg border border-background-200/70 transition-all duration-300 hover:scale-[1.01] cursor-pointer"
                >
                  <div className="relative sm:w-48 sm:h-32 flex-shrink-0 rounded-lg overflow-hidden bg-background-200">
                    <img
                      src="https://readdy.ai/api/search-image?query=Clean%20minimalist%20workspace%20with%20laptop%20displaying%20spreadsheet%20data%20and%20fashion%20accessories%20on%20cream%20desk%2C%20soft%20natural%20daylight%2C%20editorial%20photography%2C%20warm%20neutral%20aesthetic%2C%20lifestyle%20blog%20style&width=400&height=280&seq=shop-guide-hero-01&orientation=landscape"
                      alt="What Is a LitBuy Spreadsheet?"
                      title="What Is a LitBuy Spreadsheet? Complete Guide"
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="inline-block px-2 py-0.5 text-xs font-medium bg-primary-100 text-primary-700 rounded-full mb-2">Featured</span>
                    <h2 className="font-heading text-lg md:text-xl font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors">
                      What Is a LitBuy Spreadsheet? The Complete Introduction Guide
                    </h2>
                    <p className="mt-2 text-xs md:text-sm text-foreground-500 leading-relaxed line-clamp-2">
                      A complete introduction to LitBuy Spreadsheets, how they work, and why they are essential for smart shoppers and product researchers.
                    </p>
                    <div className="mt-3 flex items-center gap-3 text-xs text-foreground-400">
                      <span>12 min read</span>
                      <span>·</span>
                      <span>LitBuy Editorial Team</span>
                    </div>
                  </div>
                </Link>

                {/* Dynamic articles */}
                {seoArticles.map((article, index) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="group flex flex-col sm:flex-row gap-5 p-5 md:p-6 bg-background-100 rounded-lg border border-background-200/70 transition-all duration-300 hover:scale-[1.01] cursor-pointer"
                  >
                    <div className="relative sm:w-24 sm:h-24 flex-shrink-0 rounded-lg overflow-hidden bg-background-200 flex items-center justify-center">
                      <span className="font-heading text-2xl font-bold text-primary-400">{index + 1}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h2 className="font-heading text-base md:text-lg font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors">
                        {article.title}
                      </h2>
                      <p className="mt-1 text-xs md:text-sm text-foreground-500 leading-relaxed line-clamp-2">
                        {article.description}
                      </p>
                      <div className="mt-2 flex items-center gap-3 text-xs text-foreground-400">
                        <span>{article.readTime}</span>
                        <span>·</span>
                        <span>LitBuy Editorial Team</span>
                        <span className="inline-flex items-center gap-1 text-primary-600 font-medium ml-auto whitespace-nowrap">
                          Read More
                          <span className="w-3 h-3 flex items-center justify-center">
                            <i className="ri-arrow-right-line text-xs"></i>
                          </span>
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {/* CTA */}
              <div className="mt-12 p-6 md:p-8 bg-primary-500 rounded-lg text-center">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-white">
                  Ready to Start Shopping?
                </h2>
                <p className="mt-3 text-sm md:text-base text-white/85 max-w-lg mx-auto leading-relaxed">
                  Visit Kaspreadsheet now to explore thousands of products across all 15+ categories. Apply the LitBuy Spreadsheet methodology to make every purchase count.
                </p>
                <div className="mt-5 flex flex-col sm:flex-row gap-3 justify-center">
                  <a
                    href="https://kaspreadsheet.com/"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center justify-center gap-2 bg-white text-primary-700 px-6 py-3 rounded-full text-sm font-semibold hover:bg-white/95 transition-all cursor-pointer whitespace-nowrap"
                  >
                    Explore Kaspreadsheet
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-up-line"></i></span>
                  </a>
                  <Link
                    to="/"
                    className="inline-flex items-center justify-center gap-2 bg-primary-600 text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-primary-700 transition-all cursor-pointer whitespace-nowrap"
                  >
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line"></i></span>
                    Back to LitBuy Home
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}